/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useState} from "react";
import { IconButton, Tooltip } from "@mui/material";
import { ArgosEditIcon, ArgosDeleteIcon, ArgosMoreIcon } from "../../../../../../components/Icons/ArgosActionIcons";
import ArgosFlexbox from "../../../../../../components/Containers/ArgosFlexbox";
import MoreMenu from "./MoreMenu";
import {openDownloadDataSetDialog, selectCurrentStudyId, selectStudy} from "../../../../../../../redux/ducks/SensingSystemArchitectureDesign";
import {useAppDispatch, useAppSelector} from "../../../../../../../redux/hooks";

interface SensorActionsCellProps {
  cell: any;
  setDeleteSensorWarningDialogData: (cell: any) => void;
  onClickConfigure: (cell: any) => void;
  greenlineIdToRunState: Record<string, any>;
  tableButtonGray: string;
}

/**
 * This component renders the action buttons for each sensor in the table of sensors in green line app.
 */
const SensorActionsCell: React.FC<SensorActionsCellProps> = ({
  cell,
  setDeleteSensorWarningDialogData,
  onClickConfigure,
  greenlineIdToRunState,
  tableButtonGray,
}) => {
  const dispatch = useAppDispatch();
  const greenlineDatasetId = cell.row.original.greenlineDatasetId;
  const isGreenlineRunning = greenlineIdToRunState?.[greenlineDatasetId]?.inProgress ?? false;
  const dataSetState = cell.row.original.state;
  const blueLineState = cell.row.original.blueLineState?.state;
  const currentStudyId = useAppSelector(selectCurrentStudyId);
  const study = useAppSelector((state) => selectStudy(state, currentStudyId ?? ""));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMoreMenuOpen = Boolean(anchorEl)

  /** Open the More menu */
  const openMoreMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  /** Close the More menu */
  const closeMoreMenu = () => {
    setAnchorEl(null)
  }

  /** Handle Download button click */
  const handleDownload = (greenLineDataSetId: string) => {
    dispatch(openDownloadDataSetDialog({dataSetId: greenLineDataSetId}))
    closeMoreMenu()
  }

  return (
    <ArgosFlexbox noGap={true} alignItems={"center"}>
      {!study?.archive &&
      <>
      <IconButton
        size="large"
        onClick={() => {
          setDeleteSensorWarningDialogData(cell);
        }}
        disabled={isGreenlineRunning}
      >
        <ArgosDeleteIcon fontSize="medium" style={{ color: tableButtonGray }} />
      </IconButton>
      <IconButton size="large" onClick={() => onClickConfigure(cell)} disabled={isGreenlineRunning}>
        <ArgosEditIcon fontSize="medium" style={{ color: tableButtonGray }} />
      </IconButton>
      </>}
      <Tooltip title="More">
        <IconButton onClick={openMoreMenu}>
          <ArgosMoreIcon style={{ color: tableButtonGray }} />
        </IconButton>
      </Tooltip>
      <MoreMenu
        anchorEl={anchorEl}
        isOpen={isMoreMenuOpen}
        onClose={closeMoreMenu}
        handleDownload={handleDownload}
        greenlineDatasetId={greenlineDatasetId}
        isGreenlineRunning={isGreenlineRunning}
        dataSetState={dataSetState}
        blueLineState={blueLineState}
      />
    </ArgosFlexbox>
  );
};

export default SensorActionsCell;